import { SupabaseClient } from '@supabase/supabase-js';

export type AiEventType =
  | 'yellow_card' | 'red_card' | 'double_yellow_red'
  | 'verbal_abuse' | 'assault' | 'assault_attempt' | 'unsporting_behavior'
  | 'property_damage' | 'projectiles' | 'pyrotechnics' | 'pitch_invasion' | 'fan_violence'
  | 'match_delay' | 'match_stoppage' | 'match_not_played' | 'halftime_delay'
  | 'coach_absent' | 'coach_license_missing' | 'commissioner_absent' | 'commissioner_present'
  | 'doctor_present' | 'no_hot_water' | 'poor_facilities' | 'poor_organization' | 'late_arrival'
  // Report-event families added with the league catalog (infractions.report_event_type)
  | 'fans_travel_ban_violation' | 'late_team_entry' | 'late_match_sheet' | 'no_ball_boys'
  | 'match_paused_by_team';

export interface ArabicExtractedEvent {
  eventType: AiEventType;
  nameAr: string | null;
  nameFr: string | null;
  minute: number | null;
  personType: 'player' | 'staff' | 'official' | 'fan' | null;
  team: string | null;
  jerseyNumber: number | null;
  licenseNumber: string | null;
  resolvedFromRole?: boolean;
  details: string | null;
  /** Team side the AI attributed the event to (from MATCH_CONTEXT). */
  side?: 'home' | 'away' | null;
  /** League infraction code proposed by the AI; null when outside the event's family. */
  infractionCode?: string | null;
}

export interface FafMatchInfo {
  id: string;
  fafId: string;
  homeTeamName: string;
  awayTeamName: string;
  matchDate: string | null;
  matchTime: string | null;
  homeScore: number | null;
  awayScore: number | null;
  finalReportScrapedAt: string | null;
  matchId: string | null;
  /** Supabase Storage URL for the pre-match sheet (French) */
  pdfFrUrl: string | null;
  /** Supabase Storage URL for the pre-match sheet (Arabic) */
  pdfArUrl: string | null;
  /** Supabase Storage URL for the final-report PDF (French) */
  pdfFinalFrUrl: string | null;
  /** Supabase Storage URL for the final-report PDF (Arabic) */
  pdfFinalArUrl: string | null;
  /** Referee narrative report text from FAF final report */
  refereeReport: string | null;
  /** AI-extracted events from Arabic referee report */
  aiExtractedEvents: ArabicExtractedEvent[] | null;
  /** Timestamp of the last AI extraction run (fresh or cache hit) */
  aiExtractedAt: string | null;
  /** Timestamp of the last GH Actions recheck pass while the match was inside the 48h post-kickoff window */
  lastRecheckAt: string | null;
}

export type FafEventType = 'goal' | 'yellow_card' | 'red_card' | 'injury';

export interface FafMatchEvent {
  id: string;
  fafMatchId: string;
  eventType: FafEventType;
  teamName: string;
  isHomeTeam: boolean;
  minute: number | null;
  playerName: string | null;
  playerNumber: number | null;
  licenseNumber: string | null;
  playerId: string | null;
  /** Staff member resolved by faf-sync-match (D / E licences) */
  staffMemberId: string | null;
  infractionReason: string | null;
  infractionReasonAr: string | null;
  createdAt: string;
}

export type FafEventWithStatus = FafMatchEvent & { isAlreadyImported: boolean };

export interface FafSyncResult {
  success?: boolean;
  eventCount?: number;
  pending?: boolean;
  message?: string;
  error?: string;
}

function fafMatchDbToCamel(row: any): FafMatchInfo {
  return {
    id:                   row.id,
    fafId:                row.faf_id,
    homeTeamName:         row.home_team_name,
    awayTeamName:         row.away_team_name,
    matchDate:            row.match_date || null,
    matchTime:            row.match_time || null,
    homeScore:            row.home_score ?? null,
    awayScore:            row.away_score ?? null,
    finalReportScrapedAt: row.final_report_scraped_at || null,
    matchId:              row.match_id || null,
    pdfFrUrl:             row.pdf_fr_url || null,
    pdfArUrl:             row.pdf_ar_url || null,
    pdfFinalFrUrl:        row.pdf_final_fr_url || null,
    pdfFinalArUrl:        row.pdf_final_ar_url || null,
    refereeReport:        row.referee_report || null,
    aiExtractedEvents:    Array.isArray(row.ai_extracted_events) ? row.ai_extracted_events : null,
    aiExtractedAt:        row.ai_extracted_at || null,
    lastRecheckAt:        row.last_recheck_at || null,
  };
}

function fafEventDbToCamel(row: any): FafMatchEvent {
  return {
    id:                  row.id,
    fafMatchId:          row.faf_match_id,
    eventType:           row.event_type as FafEventType,
    teamName:            row.team_name,
    isHomeTeam:          row.is_home_team,
    minute:              row.minute ?? null,
    playerName:          row.player_name || null,
    playerNumber:        row.player_number ?? null,
    licenseNumber:       row.license_number || null,
    playerId:            row.player_id || null,
    staffMemberId:       row.staff_member_id || null,
    infractionReason:    row.infraction_reason || null,
    infractionReasonAr:  row.infraction_reason_ar || null,
    createdAt:           row.created_at,
  };
}

/**
 * Fetch the faf_matches row linked to an internal match.
 */
export async function fetchFafMatchForInternalMatch(
  supabase: SupabaseClient,
  internalMatchId: string,
): Promise<FafMatchInfo | null> {
  const { data, error } = await supabase
    .from('faf_matches')
    .select('*')
    .eq('match_id', internalMatchId)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[fafService] fetchFafMatchForInternalMatch error:', error.message);
    return null;
  }
  return data ? fafMatchDbToCamel(data) : null;
}

/**
 * Fetch all events for a faf_match, ordered by minute.
 */
export async function fetchFafMatchEvents(
  supabase: SupabaseClient,
  fafMatchId: string,
): Promise<FafMatchEvent[]> {
  const { data, error } = await supabase
    .from('faf_match_events')
    .select('*')
    .eq('faf_match_id', fafMatchId)
    .order('minute', { ascending: true, nullsFirst: false });

  if (error) {
    console.error('[fafService] fetchFafMatchEvents error:', error.message);
    return [];
  }
  return (data || []).map(fafEventDbToCamel);
}

/**
 * Fetch the set of faf_event_id values that have already been imported
 * as sanctions for the given internal match.
 */
export async function fetchImportedFafEventIds(
  supabase: SupabaseClient,
  internalMatchId: string,
): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('sanctions')
    .select('faf_event_id')
    .eq('match_id', internalMatchId)
    .not('faf_event_id', 'is', null)
    .eq('is_cancelled', false);

  if (error) {
    console.error('[fafService] fetchImportedFafEventIds error:', error.message);
    return new Set();
  }
  return new Set((data || []).map((r: any) => r.faf_event_id as string));
}

export interface AiExtractionResult {
  jobId?: string | null;
  events?: ArabicExtractedEvent[];
  cached?: boolean;
  /** ai_prompt_version the events were produced under. */
  version?: string;
  /** The report is empty or whitespace-only — nothing to analyse. */
  blank?: boolean;
  error?: string;
}

/**
 * Trigger the faf-extract-ai-events edge function for the given faf_matches UUID.
 */
export async function triggerAiExtraction(
  supabase: SupabaseClient,
  fafMatchId: string,
  force = true,
): Promise<AiExtractionResult> {
  try {
    const { data, error } = await supabase.functions.invoke('faf-extract-ai-events', {
      body: { fafMatchId, force },
    });
    if (error) return { error: error.message };
    return data as AiExtractionResult;
  } catch (err: any) {
    return { error: err.message ?? 'Unknown error' };
  }
}

/**
 * Trigger the faf-sync-match edge function for the given faf_matches UUID.
 */
export async function triggerFafSync(
  supabase: SupabaseClient,
  fafMatchId: string,
): Promise<FafSyncResult> {
  try {
    const { data, error } = await supabase.functions.invoke('faf-sync-match', {
      body: { fafMatchId },
    });
    if (error) return { error: error.message };
    return data as FafSyncResult;
  } catch (err: any) {
    return { error: err.message ?? 'Unknown error' };
  }
}
